import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Check, Sparkles, Link2, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo62CadenaEjecucion() {
    const [dias, setDias] = useState([
        { id: crypto.randomUUID(), dia: 1, accion: '', hora: '', completado: false },
        { id: crypto.randomUUID(), dia: 2, accion: '', hora: '', completado: false },
        { id: crypto.randomUUID(), dia: 3, accion: '', hora: '', completado: false },
    ]);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [diasConsecutivos, setDiasConsecutivos] = useState(0);
    const [puntuacion, setPuntuacion] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/cadena_ejecucion`, { withCredentials: true });
                if (data.data?.dias?.length >= 3) setDias(data.data.dias.map(d => ({ ...d, id: d.id || crypto.randomUUID() })));
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'cadena_ejecucion', data: { dias } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [dias]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [dias, saveDraft, loadingDraft, readOnly]);

    const updateDia = (index, field, value) => {
        const updated = [...dias];
        updated[index] = { ...updated[index], [field]: value };
        setDias(updated);
    };

    const addDay = () => setDias([...dias, { id: crypto.randomUUID(), dia: dias.length + 1, accion: '', hora: '', completado: false }]);
    const removeDay = (index) => { if (dias.length > 3) setDias(dias.filter((_, i) => i !== index).map((d, i) => ({ ...d, dia: i + 1 }))); };

    const completedDays = dias.filter(d => d.completado && d.accion.trim().length > 0);
    const consecutiveDays = (() => { let count = 0; for (const d of dias) { if (d.completado && d.accion.trim().length > 0) count++; else break; } return count; })();
    const canSubmit = !readOnly && (consecutiveDays >= 3);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/cadena-ejecucion/submit`, { dias }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setDiasConsecutivos(data.dias_consecutivos);
            setPuntuacion(data.puntuacion);
            setSubmitted(true);
            toast.success('Cadena de ejecución registrada');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#E8B27A] to-[#D4A06A] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Link2 className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="cadena-ejecucion-success-title">Cadena de ejecución activa</h2>
                    <p className="text-[#E8B27A] font-medium mb-4">{diasConsecutivos} días consecutivos registrados</p>
                    {puntuacion > 0 && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Consistencia: {puntuacion}/10</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#E8B27A] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#E8B27A] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="cadena_ejecucion" chapter={6} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-cadena-ejecucion">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 6</span><span className="text-xs text-[#7A8A93]">6.2</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Cadena de ejecución</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="px-3 py-1 bg-[#E8B27A]/10 text-[#E8B27A] rounded-full text-xs font-medium">{consecutiveDays} días seguidos</div>
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                        </div>
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#E8B27A] to-[#D4A06A] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Link2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Construye tu cadena diaria</CardTitle><p className="text-xs sm:text-sm text-white/70">Registra al menos 3 días consecutivos de acción</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-4">
                        {dias.map((dia, index) => (
                            <div key={dia.id} className={`p-4 rounded-xl border ${dia.completado && dia.accion.trim().length > 0 ? 'bg-[#5FAF9D]/5 border-[#5FAF9D]/30' : 'bg-white border-[#DCE3E8]'}`} data-testid={`dia-entry-${index}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-[#3E5F6E]">Día {dia.dia}</span>
                                        {dia.completado && dia.accion.trim().length > 0 ? <CheckCircle className="h-4 w-4 text-[#5FAF9D]" /> : <XCircle className="h-4 w-4 text-[#DCE3E8]" />}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-[#7A8A93]">
                                            <input type="checkbox" checked={dia.completado} onChange={e => updateDia(index, 'completado', e.target.checked)} className="rounded border-[#DCE3E8] text-[#5FAF9D] focus:ring-[#5FAF9D]" />
                                            Completado
                                        </label>
                                        {dias.length > 3 && <button onClick={() => removeDay(index)} className="text-[#D07F7E] hover:text-[#C06060]"><Trash2 className="h-4 w-4" /></button>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div><Label className="text-xs text-[#7A8A93]">Acción realizada</Label><Input value={dia.accion} onChange={e => updateDia(index, 'accion', e.target.value)} placeholder="¿Qué hiciste?" className="border-[#DCE3E8] rounded-lg text-sm mt-1" /></div>
                                    <div><Label className="text-xs text-[#7A8A93]">Hora</Label><Input value={dia.hora} onChange={e => updateDia(index, 'hora', e.target.value)} placeholder="Ej: 9:00 AM" className="border-[#DCE3E8] rounded-lg text-sm mt-1" /></div>
                                </div>
                            </div>
                        ))}
                        <Button variant="outline" onClick={addDay} className="w-full border-dashed border-[#DCE3E8] text-[#7A8A93] hover:text-[#3E5F6E]" data-testid="add-day-btn"><Plus className="h-4 w-4 mr-2" />Agregar día</Button>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-cadena-ejecucion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Registrar cadena</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
